import React from 'react';
import { Link } from 'react-router-dom';
import { Shield } from 'lucide-react';
import Header from '../components/Header';

const PrivacyPolicyPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-midnight">
      {/* Header */}
      <Header />

      {/* Hero Section */}
      <section className="bg-midnight text-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-goldie w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
            <Shield className="w-8 h-8 text-black" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-6 font-display">
            Privacy Policy
          </h1>
          <p className="text-xl text-gray-300 mb-4">
            Last updated: {new Date().toLocaleDateString('en-US', { 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </p>
          <p className="text-lg text-gray-400">
            We are committed to protecting your privacy and ensuring the security of your personal information.
          </p>
        </div>
      </section>

      {/* Privacy Content */}
      <section className="py-16 bg-sky text-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white p-8 rounded-lg border-2 border-black">
            
            {/* Introduction */}
            <div className="mb-8">
              <h2 className="text-2xl font-bold mb-4 font-display">1. Introduction</h2>
              <p className="text-gray-700 mb-4">
                BandCam.io ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy 
                explains how we collect, use, disclose, and safeguard your information when you use our platform 
                that connects school bands with families seeking personalized video messages.
              </p>
              <p className="text-gray-700">
                By using our service, you consent to the data practices described in this policy. If you do not 
                agree with our policies and practices, please do not use our service.
              </p>
            </div>

            {/* Information We Collect */}
            <div className="mb-8">
              <h2 className="text-2xl font-bold mb-4 font-display">2. Information We Collect</h2>
              
              <h3 className="text-xl font-semibold mb-3 text-goldie">Personal Information</h3>
              <p className="text-gray-700 mb-4">
                We collect information you provide directly to us, including:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4 mb-6">
                <li><strong>Account Information:</strong> Name, email address, phone number, and password</li>
                <li><strong>Profile Information:</strong> School name, address, band type, and size</li>
                <li><strong>Order Information:</strong> Video requests, personalized messages, and payment details</li>
                <li><strong>Communication:</strong> Messages, feedback, and support requests</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3 text-goldie">Automatically Collected Information</h3>
              <p className="text-gray-700 mb-4">
                When you use our service, we automatically collect:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li><strong>Device Information:</strong> IP address, browser type, operating system, and device identifiers</li>
                <li><strong>Usage Data:</strong> Pages visited, time spent, and interactions with our platform</li>
                <li><strong>Technical Data:</strong> Log files, error reports, and performance metrics</li>
                <li><strong>Location Data:</strong> General location information based on IP address</li>
              </ul>
            </div>

            {/* How We Use Information */}
            <div className="mb-8">
              <h2 className="text-2xl font-bold mb-4 font-display">3. How We Use Your Information</h2>
              <p className="text-gray-700 mb-4">
                We use the information we collect to:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>Provide, maintain, and improve our services</li>
                <li>Process orders and facilitate payments</li>
                <li>Connect customers with appropriate school bands</li>
                <li>Send important service updates and notifications</li>
                <li>Respond to customer support requests</li>
                <li>Analyze usage patterns to enhance user experience</li>
                <li>Prevent fraud and ensure platform security</li>
                <li>Comply with legal obligations</li>
              </ul>
            </div>

            {/* Information Sharing */}
            <div className="mb-8">
              <h2 className="text-2xl font-bold mb-4 font-display">4. Information Sharing and Disclosure</h2>
              <p className="text-gray-700 mb-4">
                We do not sell, trade, or rent your personal information to third parties. We may share your 
                information in the following circumstances:
              </p>
              
              <h3 className="text-xl font-semibold mb-3 text-goldie">Service Providers</h3>
              <p className="text-gray-700 mb-4">
                We may share information with trusted third-party service providers who assist us in:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4 mb-6">
                <li>Payment processing and financial services</li>
                <li>Cloud hosting and data storage</li>
                <li>Customer support and communication</li>
                <li>Analytics and performance monitoring</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3 text-goldie">Legal Requirements</h3>
              <p className="text-gray-700 mb-4">
                We may disclose your information if required by law or in response to:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4 mb-6">
                <li>Valid legal requests from law enforcement</li>
                <li>Court orders or subpoenas</li>
                <li>Government investigations</li>
                <li>Protection of our rights and safety</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3 text-goldie">Business Transfers</h3>
              <p className="text-gray-700">
                In the event of a merger, acquisition, or sale of assets, your information may be transferred 
                as part of the business transaction, subject to the same privacy protections.
              </p>
            </div>

            {/* Data Security */}
            <div className="mb-8">
              <h2 className="text-2xl font-bold mb-4 font-display">5. Data Security</h2>
              <p className="text-gray-700 mb-4">
                We implement appropriate technical and organizational security measures to protect your personal 
                information, including:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>Encryption of data in transit and at rest</li>
                <li>Regular security assessments and updates</li>
                <li>Access controls and authentication measures</li>
                <li>Secure data centers and infrastructure</li>
                <li>Employee training on data protection</li>
                <li>Incident response and breach notification procedures</li>
              </ul>
              <p className="text-gray-700 mt-4">
                However, no method of transmission over the internet or electronic storage is 100% secure. 
                While we strive to protect your information, we cannot guarantee absolute security.
              </p>
            </div>

            {/* Data Retention */}
            <div className="mb-8">
              <h2 className="text-2xl font-bold mb-4 font-display">6. Data Retention</h2>
              <p className="text-gray-700 mb-4">
                We retain your personal information for as long as necessary to:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4 mb-4">
                <li>Provide our services and maintain your account</li>
                <li>Comply with legal obligations</li>
                <li>Resolve disputes and enforce agreements</li>
                <li>Improve our services and user experience</li>
              </ul>
              <p className="text-gray-700">
                When we no longer need your information, we will securely delete or anonymize it in accordance 
                with our data retention policies.
              </p>
            </div>

            {/* Your Rights */}
            <div className="mb-8">
              <h2 className="text-2xl font-bold mb-4 font-display">7. Your Privacy Rights</h2>
              <p className="text-gray-700 mb-4">
                Depending on your location, you may have the following rights regarding your personal information:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li><strong>Access:</strong> Request a copy of your personal information</li>
                <li><strong>Correction:</strong> Update or correct inaccurate information</li>
                <li><strong>Deletion:</strong> Request deletion of your personal information</li>
                <li><strong>Portability:</strong> Receive your data in a portable format</li>
                <li><strong>Restriction:</strong> Limit how we process your information</li>
                <li><strong>Objection:</strong> Object to certain processing activities</li>
                <li><strong>Withdrawal:</strong> Withdraw consent for data processing</li>
              </ul>
              <p className="text-gray-700 mt-4">
                To exercise these rights, please contact us using the information provided below. We will respond 
                to your request within 30 days.
              </p>
            </div>

            {/* Cookies and Tracking */}
            <div className="mb-8">
              <h2 className="text-2xl font-bold mb-4 font-display">8. Cookies and Tracking Technologies</h2>
              <p className="text-gray-700 mb-4">
                We use cookies and similar tracking technologies to:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4 mb-4">
                <li>Remember your preferences and settings</li>
                <li>Analyze website traffic and usage patterns</li>
                <li>Improve our services and user experience</li>
                <li>Provide personalized content and recommendations</li>
                <li>Ensure platform security and prevent fraud</li>
              </ul>
              <p className="text-gray-700">
                You can control cookie settings through your browser preferences. However, disabling certain 
                cookies may affect the functionality of our service.
              </p>
            </div>

            {/* Children's Privacy */}
            <div className="mb-8">
              <h2 className="text-2xl font-bold mb-4 font-display">9. Children's Privacy</h2>
              <p className="text-gray-700 mb-4">
                Our service is not intended for children under 13 years of age. We do not knowingly collect 
                personal information from children under 13.
              </p>
              <p className="text-gray-700">
                If you are a parent or guardian and believe your child has provided us with personal information, 
                please contact us immediately. We will take steps to remove such information from our records.
              </p>
            </div>

            {/* International Data Transfers */}
            <div className="mb-8">
              <h2 className="text-2xl font-bold mb-4 font-display">10. International Data Transfers</h2>
              <p className="text-gray-700 mb-4">
                Your information may be transferred to and processed in countries other than your own. We ensure 
                that such transfers comply with applicable data protection laws and implement appropriate safeguards.
              </p>
              <p className="text-gray-700">
                For users in the European Union, we rely on adequacy decisions, standard contractual clauses, 
                or other appropriate safeguards for international data transfers.
              </p>
            </div>

            {/* Third-Party Links */}
            <div className="mb-8">
              <h2 className="text-2xl font-bold mb-4 font-display">11. Third-Party Links</h2>
              <p className="text-gray-700">
                Our service may contain links to third-party websites or services. We are not responsible for 
                the privacy practices or content of these third parties. We encourage you to review their 
                privacy policies before providing any personal information.
              </p>
            </div>

            {/* Changes to Privacy Policy */}
            <div className="mb-8">
              <h2 className="text-2xl font-bold mb-4 font-display">12. Changes to This Privacy Policy</h2>
              <p className="text-gray-700 mb-4">
                We may update this Privacy Policy from time to time to reflect changes in our practices or 
                applicable laws. We will notify you of any material changes by:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>Posting the updated policy on our website</li>
                <li>Sending email notifications to registered users</li>
                <li>Displaying prominent notices on our platform</li>
              </ul>
              <p className="text-gray-700 mt-4">
                Your continued use of our service after changes become effective constitutes acceptance of the 
                updated Privacy Policy.
              </p>
            </div>

            {/* Contact Information */}
            <div className="mb-8">
              <h2 className="text-2xl font-bold mb-4 font-display">13. Contact Us</h2>
              <p className="text-gray-700 mb-4">
                If you have any questions about this Privacy Policy or our data practices, please contact us:
              </p>
              <div className="bg-gray-50 p-4 rounded-lg border-2 border-gray-200">
                <p className="text-gray-700"><strong>Email:</strong> support@bandcam.io</p>
                <p className="text-gray-700"><strong>Phone:</strong> (512) 707-9316</p>
                <p className="text-gray-700"><strong>Address:</strong> 10613 Pinehurst Dr. Austin, TX 78747</p>
              </div>
            </div>

            {/* Effective Date */}
            <div className="border-t-2 border-gray-200 pt-6">
              <p className="text-sm text-gray-600 text-center">
                This Privacy Policy is effective as of {new Date().toLocaleDateString('en-US', { 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}.
              </p>
            </div>

          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-midnight text-white border-t-2 border-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="col-span-1 md:col-span-2">
              <div className="flex items-center mb-4">
                <img 
                  src="/logo.png" 
                  alt="BandCam.io Logo" 
                  className="w-8 h-8 object-contain"
                />
                <span className="ml-2 text-xl font-bold">BandCam.io</span>
              </div>
              <p className="text-gray-300 mb-4">
                Connecting families with their school bands to create unforgettable personalized video messages for special occasions.
              </p>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold mb-4">Quick Links</h3>
              <ul className="space-y-2">
                <li><Link to="/schools" className="text-gray-300 hover:text-white">Find Your School</Link></li>
                <li><Link to="/about" className="text-gray-300 hover:text-white">About Us</Link></li>
                <li><Link to="/contact" className="text-gray-300 hover:text-white">Contact</Link></li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold mb-4">Legal</h3>
              <ul className="space-y-2">
                <li><Link to="/terms" className="text-gray-300 hover:text-white">Terms of Service</Link></li>
                <li><Link to="/privacy" className="text-goldie hover:text-white">Privacy Policy</Link></li>
                <li><Link to="/help" className="text-gray-300 hover:text-white">Help Center</Link></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-700 mt-8 pt-8 text-center text-gray-300">
            <p>&copy; {new Date().getFullYear()} BandCam.io. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicyPage;
